<template>
  <div class="whatsapp-logs-container p-6 bg-slate-50 min-h-screen flex flex-col">
    <!-- Top Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <!-- Total Sent Card -->
      <div
        class="stat-card bg-white rounded-xl p-4 shadow-sm border border-slate-100 flex items-center justify-between transition-all duration-300 hover:shadow-md hover:-translate-y-0.5"
      >
        <div>
          <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Sent</p>
          <h3 class="text-2xl font-bold text-slate-800 mt-1">{{ stats.total }}</h3>
        </div>
        <div class="p-2 bg-blue-50 text-blue-600 rounded-lg">
          <v-icon size="22">mdi-send</v-icon>
        </div>
      </div>

      <!-- Delivered Card -->
      <div
        class="stat-card bg-white rounded-xl p-4 shadow-sm border border-slate-100 flex items-center justify-between transition-all duration-300 hover:shadow-md hover:-translate-y-0.5"
      >
        <div>
          <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider">Delivered</p>
          <h3 class="text-2xl font-bold text-indigo-600 mt-1">
            {{ stats.delivered }}
            <span class="text-xs font-normal text-slate-400 ml-1"
              >({{ stats.deliveredRate }}%)</span
            >
          </h3>
        </div>
        <div class="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
          <v-icon size="22">mdi-check-all</v-icon>
        </div>
      </div>

      <!-- Read Card -->
      <div
        class="stat-card bg-white rounded-xl p-4 shadow-sm border border-slate-100 flex items-center justify-between transition-all duration-300 hover:shadow-md hover:-translate-y-0.5"
      >
        <div>
          <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider">Read Rate</p>
          <h3 class="text-2xl font-bold text-emerald-600 mt-1">
            {{ stats.read }}
            <span class="text-xs font-normal text-slate-400 ml-1">({{ stats.readRate }}%)</span>
          </h3>
        </div>
        <div class="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
          <v-icon size="22" color="emerald">mdi-eye</v-icon>
        </div>
      </div>

      <!-- Replies Card -->
      <div
        class="stat-card bg-white rounded-xl p-4 shadow-sm border border-slate-100 flex items-center justify-between transition-all duration-300 hover:shadow-md hover:-translate-y-0.5"
      >
        <div>
          <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider">
            Replies Received
          </p>
          <h3 class="text-2xl font-bold text-pink-600 mt-1">{{ stats.replies }}</h3>
        </div>
        <div class="p-2 bg-pink-50 text-pink-600 rounded-lg">
          <v-icon size="22">mdi-forum</v-icon>
        </div>
      </div>
    </div>

    <!-- Active Feed Header & Table -->
    <div class="bg-white shadow-sm border border-slate-100 overflow-hidden justify-between">
      <!-- Table Actions Header -->
      <div
        class="p-6 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
      >
        <div>
          <h2 class="text-xl font-bold text-slate-800">WhatsApp Delivery History</h2>
        </div>
        <div class="flex flex-wrap items-center gap-3 justify-end">
          <!-- Search field -->
          <div class="w-60">
            <v-text-field
              v-model="localSearch"
              prepend-inner-icon="mdi-magnify"
              label="Search logs..."
              outlined
              dense
              hide-details
              clearable
              class="rounded-lg bg-white"
            ></v-text-field>
          </div>

          <!-- Status select -->
          <div class="w-48">
            <v-select
              v-model="selectedStatus"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              label="Select Status"
              outlined
              dense
              hide-details
              clearable
              class="rounded-lg bg-white"
            ></v-select>
          </div>

          <!-- Date picker -->
          <div class="w-48">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="selectedDate"
                  label="Select Date"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  hide-details
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  class="rounded-lg bg-white"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="selectedDate"
                no-title
                scrollable
                @change="dateMenu = false"
              ></v-date-picker>
            </v-menu>
          </div>

          <v-btn
            @click="fetchLogs"
            :loading="loading"
            color="primary"
            variant="outlined"
            class="normal-case rounded-xl font-medium border-slate-200 hover:bg-slate-50 text-slate-600 px-4 py-2"
          >
            <v-icon left size="18">mdi-refresh</v-icon>
            Sync Now
          </v-btn>
        </div>
      </div>

      <!-- Logs Data Table -->
      <div
        v-if="loading && logs.length === 0"
        class="flex flex-col items-center justify-center py-20"
      >
        <v-progress-circular
          indeterminate
          color="blue"
          :size="48"
          :width="4"
          class="mb-4"
        ></v-progress-circular>
        <span class="text-slate-400 text-sm font-medium">Loading transmission records...</span>
      </div>

      <div
        v-else-if="filteredLogs.length === 0"
        class="flex flex-col items-center justify-center py-20 text-center px-4"
      >
        <div class="p-4 bg-slate-50 rounded-full text-slate-300 mb-4">
          <v-icon size="48">mdi-email-search-outline</v-icon>
        </div>
        <h4 class="text-lg font-bold text-slate-700">No logs found</h4>
        <p class="text-sm text-slate-400 mt-1 max-w-sm">
          No WhatsApp message records matched your search terms or have been sent yet.
        </p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-slate-50/70 border-b border-slate-100">
              <th class="p-4 text-xs font-semibold text-slate-400 uppercase tracking-wider pl-6">
                Recipient
              </th>
              <th class="p-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Trigger Module
              </th>
              <th class="p-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Message Content
              </th>
              <th class="p-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Delivery Status
              </th>
              <th class="p-4 text-xs font-semibold text-slate-400 uppercase tracking-wider pr-6">
                Webhook User Reply
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            <tr
              v-for="log in paginatedLogs"
              :key="log._id"
              class="hover:bg-slate-50/50 transition-colors duration-150"
            >
              <!-- Recipient Info -->
              <td class="p-4 pl-6">
                <div class="flex flex-col">
                  <span class="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                    <v-icon size="16" class="text-slate-400">
                      {{ log.customerName ? 'mdi-account' : 'mdi-cellphone' }}
                    </v-icon>
                    {{ log.customerName || '+' + formatPhone(log.triggeredTo) }}
                  </span>
                  <span
                    v-if="log.customerName"
                    class="text-[11px] text-slate-500 mt-1 flex items-center gap-1"
                  >
                    <v-icon size="12" class="text-slate-400">mdi-cellphone</v-icon>
                    +{{ formatPhone(log.triggeredTo) }}
                  </span>
                  <span class="text-xs text-slate-400 mt-1 flex items-center gap-1">
                    <v-icon size="14" class="text-slate-300">mdi-clock-outline</v-icon>
                    {{ formatDateTime(log.createdAt) }}
                  </span>
                </div>
              </td>

              <!-- Trigger Module -->
              <td class="p-4">
                <span
                  class="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200 uppercase tracking-wider"
                >
                  {{ log.triggerType || 'manual' }}
                </span>
              </td>

              <!-- Message Body -->
              <td class="p-4 max-w-xs">
                <div
                  class="text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-xl p-3.5 m-2 leading-relaxed relative overflow-hidden group"
                >
                  <div class="line-clamp-3 whitespace-pre-line" v-html="log.message"></div>
                  <v-dialog width="500px">
                    <template #activator="{ on, attrs }">
                      <button
                        v-bind="attrs"
                        v-on="on"
                        class="text-xs text-blue-600 font-bold hover:underline mt-2 flex items-center gap-1"
                      >
                        View Full Message
                        <v-icon size="12" color="blue">mdi-arrow-right</v-icon>
                      </button>
                    </template>
                    <v-card class="rounded-2xl p-6">
                      <div class="flex items-center justify-between mb-4">
                        <h3 class="text-lg font-bold text-slate-800">Complete Sent Message</h3>
                      </div>
                      <div
                        class="p-4 bg-slate-50 border border-slate-100 rounded-xl text-sm text-slate-600 whitespace-pre-line leading-relaxed max-h-96 overflow-y-auto"
                        v-html="log.message"
                      ></div>
                    </v-card>
                  </v-dialog>
                </div>
              </td>

              <!-- Status Chip -->
              <td class="p-4">
                <div class="flex flex-col m-2 gap-1">
                  <span
                    :class="statusClass(log.status)"
                    class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold w-max shadow-sm"
                  >
                    <v-icon size="14" :color="statusIconColor(log.status)">{{
                      statusIcon(log.status)
                    }}</v-icon>
                    {{ formatStatus(log.status) }}
                  </span>
                  <span v-if="log.messageId" class="text-[10px] text-slate-400 font-mono mt-1"
                    >ID: {{ log.messageId }}</span
                  >
                </div>
              </td>

              <!-- Webhook User Reply -->
              <td class="p-4 pr-6">
                <div v-if="log.chat && log.chat.length > 0" class="flex flex-col gap-2 m-2">
                  <!-- Preview of the last message in chat -->
                  <div
                    :class="[
                      'rounded-xl p-3 text-xs leading-relaxed flex flex-col gap-1 border shadow-sm',
                      getLastMessage(log).sender === 'system' ||
                      getLastMessage(log).sender === 'org'
                        ? 'bg-indigo-50/50 text-indigo-900 border-indigo-100'
                        : 'bg-pink-50/50 text-slate-700 border-pink-100'
                    ]"
                  >
                    <span
                      class="text-[9px] font-bold flex items-center gap-1 uppercase tracking-wider"
                      :class="
                        getLastMessage(log).sender === 'system' ||
                        getLastMessage(log).sender === 'org'
                          ? 'text-indigo-600'
                          : 'text-pink-600'
                      "
                    >
                      <v-icon
                        size="10"
                        :color="
                          getLastMessage(log).sender === 'system' ||
                          getLastMessage(log).sender === 'org'
                            ? 'indigo'
                            : 'pink'
                        "
                      >
                        {{
                          getLastMessage(log).sender === 'system' ||
                          getLastMessage(log).sender === 'org'
                            ? 'mdi-send-circle'
                            : 'mdi-message-reply-text'
                        }}
                      </v-icon>
                      Last:
                      {{
                        getLastMessage(log).sender === 'system' ||
                        getLastMessage(log).sender === 'org'
                          ? 'System'
                          : log.customerName || 'Customer'
                      }}
                    </span>
                    <span class="font-medium text-slate-600 line-clamp-2"
                      >"{{ getLastMessage(log).message }}"</span
                    >
                  </div>

                  <!-- Button to open full thread -->
                  <v-btn
                    small
                    text
                    color="primary"
                    class="normal-case text-[10px] font-bold self-start p-0 h-auto hover:bg-transparent"
                    @click="openChatThread(log)"
                  >
                    <v-icon left size="12">mdi-forum-outline</v-icon>
                    View Full Thread ({{ log.chat.length }})
                  </v-btn>
                </div>

                <div v-else-if="log.replyMessage" class="flex flex-col gap-2">
                  <div
                    class="reply-bubble relative bg-pink-50/50 border border-pink-100 text-slate-700 rounded-2xl p-3 text-sm flex flex-col gap-1 leading-relaxed"
                  >
                    <span
                      class="text-[10px] font-bold text-pink-600 flex items-center gap-1 uppercase tracking-wider"
                    >
                      <v-icon size="12" color="pink">mdi-message-reply-text</v-icon>
                      {{ log.customerName || 'Customer' }} Response
                    </span>
                    <span class="font-medium mt-0.5 text-slate-600 italic line-clamp-2"
                      >"{{ log.replyMessage }}"</span
                    >
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-[10px] text-slate-400">
                      Received: {{ formatDateTime(log.replyReceivedAt) }}
                    </span>
                    <v-btn
                      small
                      text
                      color="primary"
                      class="normal-case text-[10px] font-bold p-0 h-auto hover:bg-transparent"
                      @click="openChatThread(log)"
                    >
                      <v-icon left size="12">mdi-forum-outline</v-icon>
                      View Thread
                    </v-btn>
                  </div>
                </div>

                <div
                  v-else
                  class="text-xs text-slate-400 italic flex items-center gap-1.5 py-2 pl-2"
                >
                  <span class="w-1.5 h-1.5 bg-slate-300 rounded-full"></span>
                  Waiting for reply...
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Controls -->
      <div
        v-if="filteredLogs.length"
        class="p-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white"
      >
        <div class="text-xs font-semibold text-slate-500">
          Showing {{ filteredLogs.length > 0 ? (page - 1) * itemsPerPage + 1 : 0 }} to
          {{ Math.min(page * itemsPerPage, filteredLogs.length) }} of
          {{ filteredLogs.length }} records
        </div>
        <div class="flex items-center gap-4">
          <v-select
            v-model="itemsPerPage"
            :items="[5, 10, 20, 50]"
            outlined
            dense
            hide-details
            class="rounded-lg max-w-[80px] text-xs bg-slate-50 pagination-select"
            @change="page = 1"
          ></v-select>
          <div class="flex items-center gap-1">
            <v-btn
              icon
              small
              class="border border-slate-200 rounded-lg hover:bg-slate-50"
              :disabled="page === 1"
              @click="prevPage"
            >
              <v-icon size="16">mdi-chevron-left</v-icon>
            </v-btn>
            <span class="text-xs font-bold text-slate-600 px-2"
              >Page {{ page }} of {{ totalPages }}</span
            >
            <v-btn
              icon
              small
              class="border border-slate-200 rounded-lg hover:bg-slate-50"
              :disabled="page === totalPages"
              @click="nextPage"
            >
              <v-icon size="16">mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- Chat Thread Dialog -->
    <v-dialog v-model="chatDialog" max-width="700px" scrollable>
      <v-card
        class="chat-card rounded-2xl border border-slate-100 shadow-xl bg-slate-50 flex flex-col max-h-[600px] p-4"
      >
        <!-- Dialog Header -->
        <v-card-title
          class="bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between"
        >
          <div class="flex items-center gap-3">
            <v-avatar color="indigo lighten-5" size="40">
              <v-icon color="indigo">mdi-whatsapp</v-icon>
            </v-avatar>
            <div class="flex flex-col">
              <span
                class="text-sm font-bold text-slate-800 leading-tight"
                v-if="activeChatLog?.customerName"
              >
                {{ activeChatLog.customerName }} (+{{ formatPhone(activeChatLog.triggeredTo) }})
              </span>
              <span v-else class="text-[11px] text-slate-500 font-semibold mt-0.5">
                +{{ formatPhone(activeChatLog.triggeredTo) }}
              </span>
              <span
                class="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-0.5"
              >
                Conversation Thread
              </span>
            </div>
          </div>
          <v-btn
            icon
            color="slate"
            @click="chatDialog = false"
            class="hover:bg-slate-100 rounded-lg"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Chat Messages -->
        <v-card-text ref="chatContainer" class="flex-grow p-6 overflow-y-auto flex flex-col gap-4">
          <div
            v-for="(msg, index) in activeChatLog.chat ||
            (activeChatLog.replyMessage
              ? [
                  {
                    sender: 'system',
                    message: activeChatLog.message,
                    timestamp: activeChatLog.createdAt
                  },
                  {
                    sender: 'customer',
                    message: activeChatLog.replyMessage,
                    timestamp: activeChatLog.replyReceivedAt
                  }
                ]
              : [])"
            :key="index"
            :class="[
              'rounded-2xl p-3.5 text-xs leading-relaxed max-w-[85%] flex flex-col gap-1 shadow-sm border relative',
              msg.sender === 'system' || msg.sender === 'org'
                ? 'bg-indigo-50 text-indigo-900 border-indigo-100 self-end rounded-tr-none'
                : 'bg-white text-slate-700 border-slate-100 self-start rounded-tl-none'
            ]"
          >
            <!-- Sender Identifier -->
            <span
              class="text-[9px] font-bold flex items-center gap-1 uppercase tracking-wider mb-0.5"
              :class="
                msg.sender === 'system' || msg.sender === 'org'
                  ? 'text-indigo-600'
                  : 'text-pink-600'
              "
            >
              <v-icon
                size="10"
                :color="msg.sender === 'system' || msg.sender === 'org' ? 'indigo' : 'pink'"
              >
                {{
                  msg.sender === 'system' || msg.sender === 'org'
                    ? 'mdi-send-circle'
                    : 'mdi-message-reply-text'
                }}
              </v-icon>
              {{
                msg.sender === 'system' || msg.sender === 'org'
                  ? 'System'
                  : activeChatLog.customerName || 'Customer'
              }}
            </span>
            <span
              class="font-medium text-slate-700 whitespace-pre-wrap"
              v-html="msg.message"
            ></span>
            <span class="text-[8px] text-slate-400 self-end mt-1 font-semibold">
              {{ formatDateTime(msg.timestamp) }}
            </span>
          </div>
        </v-card-text>

        <!-- Reply Input Area -->
        <div class="bg-white border-t border-slate-100 px-6 py-4 flex items-center gap-2">
          <v-text-field
            v-model="replyText"
            placeholder="Type a reply..."
            outlined
            dense
            hide-details
            class="rounded-xl flex-grow bg-slate-50 text-xs"
            @keyup.enter="sendReply"
            :disabled="replyLoading"
          ></v-text-field>
          <v-btn
            color="indigo"
            fab
            x-small
            dark
            class="shadow-none rounded-xl"
            @click="sendReply"
            :loading="replyLoading"
            :disabled="!replyText.trim()"
          >
            <v-icon size="18">mdi-send</v-icon>
          </v-btn>
        </div>

        <!-- Dialog Footer -->
        <v-card-actions class="bg-white border-t border-slate-100 p-4 justify-between">
          <span class="text-[10px] text-slate-400 font-medium">
            Session: {{ activeChatLog.messageId ? 'Active' : 'Closed' }}
          </span>
          <v-btn
            text
            color="primary"
            class="font-bold normal-case text-xs rounded-xl"
            @click="chatDialog = false"
          >
            Close Thread
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { debounce } from '@/utils/common'

export default {
  name: 'WhatsAppLogs',
  props: {
    search: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      logs: [],
      loading: false,
      localSearch: '',
      selectedStatus: '',
      selectedDate: new Date().toLocaleDateString('en-CA'),
      dateMenu: false,
      page: 1,
      itemsPerPage: 10,
      chatDialog: false,
      activeChatLog: {},
      replyText: '',
      replyLoading: false,
      statusOptions: [
        { text: 'All Statuses', value: '' },
        { text: 'Read', value: 'read' },
        { text: 'Delivered', value: 'delivered' },
        { text: 'Sent', value: 'sent' },
        { text: 'Queued', value: 'init' },
        { text: 'Failed', value: 'failed' }
      ]
    }
  },

  watch: {
    search: {
      handler(val) {
        this.localSearch = val || ''
        this.page = 1
      },
      immediate: true
    },
    localSearch() {
      this.page = 1
    },
    selectedStatus() {
      this.page = 1
    },
    selectedDate() {
      this.page = 1
      this.fetchLogs()
    }
  },
  computed: {
    filteredLogs() {
      let result = this.logs

      // 1. Search term filter
      if (this.localSearch && this.localSearch.trim() !== '') {
        const term = this.localSearch.toLowerCase()
        result = result.filter((log) => {
          return (
            (log.triggeredTo && log.triggeredTo.toLowerCase().includes(term)) ||
            (log.message && log.message.toLowerCase().includes(term)) ||
            (log.replyMessage && log.replyMessage.toLowerCase().includes(term)) ||
            (log.triggerType && log.triggerType.toLowerCase().includes(term))
          )
        })
      }

      // 2. Status filter
      if (this.selectedStatus) {
        const targetStatus = this.selectedStatus.toLowerCase()
        result = result.filter((log) => {
          const currentStatus = (log.status || 'init').toLowerCase()
          return currentStatus === targetStatus
        })
      }

      return result
    },
    paginatedLogs() {
      const start = (this.page - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredLogs.slice(start, end)
    },
    totalPages() {
      return Math.ceil(this.filteredLogs.length / this.itemsPerPage)
    },
    stats() {
      const total = this.logs.length
      if (total === 0) {
        return { total: 0, delivered: 0, deliveredRate: 0, read: 0, readRate: 0, replies: 0 }
      }

      const delivered = this.logs.filter((l) =>
        ['delivered', 'read'].includes(l.status?.toLowerCase())
      ).length

      const read = this.logs.filter((l) => l.status?.toLowerCase() === 'read').length

      const replies = this.logs.filter((l) => !!l.replyMessage).length

      return {
        total,
        delivered,
        deliveredRate: Math.round((delivered / total) * 100),
        read,
        readRate: Math.round((read / total) * 100),
        replies
      }
    }
  },
  methods: {
    async fetchLogs() {
      this.loading = true
      try {
        const params = {
          limit: 10000
        }
        if (this.selectedDate) {
          params.date = this.selectedDate
        }
        const response = await this.$axios.get('/whatsapp/templates/messages', { params })
        if (response.data && response.data.succes) {
          this.logs = response.data.messages || []
        }
      } catch (error) {
        console.error('Error fetching WhatsApp logs:', error)
        this.$toast.error('Failed to fetch WhatsApp logs')
      } finally {
        this.loading = false
      }
    },
    getLastMessage(log) {
      if (log.chat && log.chat.length > 0) {
        return log.chat[log.chat.length - 1]
      }
      return { sender: 'customer', message: log.replyMessage || '' }
    },
    openChatThread(log) {
      this.activeChatLog = log
      this.chatDialog = true
      this.replyText = ''
      setTimeout(() => {
        this.scrollToBottom()
      }, 100)
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatContainer
        if (container && container.$el) {
          container.$el.scrollTop = container.$el.scrollHeight
        } else if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    async sendReply() {
      if (!this.replyText.trim() || this.replyLoading) return
      this.replyLoading = true
      try {
        const response = await this.$axios.post('/whatsapp/templates/reply', {
          messageId: this.activeChatLog._id,
          message: this.replyText
        })
        if (response.data && response.data.succes) {
          this.$toast.success('Reply sent successfully')
          if (!this.activeChatLog.chat) {
            this.$set(this.activeChatLog, 'chat', [])
          }
          this.activeChatLog.chat = response.data.chat
          this.replyText = ''
          setTimeout(() => {
            this.scrollToBottom()
          }, 50)
        }
      } catch (error) {
        console.error('Error sending reply:', error)
        this.$toast.error(error.response?.data?.message || 'Failed to send reply')
      } finally {
        this.replyLoading = false
      }
    },
    formatPhone(phone) {
      if (!phone) return ''
      return phone.replace(/^91/, '91 ')
    },
    formatDateTime(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    },
    formatStatus(status) {
      if (!status) return 'INIT'
      const s = status.toLowerCase()
      if (s === 'init') return 'QUEUED'
      return s.toUpperCase()
    },
    statusClass(status) {
      if (!status) return 'bg-slate-50 text-slate-500 border border-slate-200'
      const s = status.toLowerCase()
      switch (s) {
        case 'read':
          return 'bg-emerald-50 text-emerald-700 border border-emerald-100'
        case 'delivered':
          return 'bg-indigo-50 text-indigo-700 border border-indigo-100'
        case 'sent':
          return 'bg-blue-50 text-blue-700 border border-blue-100'
        case 'failed':
          return 'bg-rose-50 text-rose-700 border border-rose-100'
        default:
          return 'bg-slate-50 text-slate-600 border border-slate-200'
      }
    },
    statusIcon(status) {
      if (!status) return 'mdi-send-clock-outline'
      const s = status.toLowerCase()
      switch (s) {
        case 'read':
          return 'mdi-check-all'
        case 'delivered':
          return 'mdi-check-all'
        case 'sent':
          return 'mdi-check'
        case 'failed':
          return 'mdi-alert-circle-outline'
        default:
          return 'mdi-send-clock-outline'
      }
    },
    statusIconColor(status) {
      if (!status) return 'grey'
      const s = status.toLowerCase()
      switch (s) {
        case 'read':
          return '#059669' // emerald-600
        case 'delivered':
          return '#4f46e5' // indigo-600
        case 'sent':
          return '#2563eb' // blue-600
        case 'failed':
          return '#e11d48' // rose-600
        default:
          return 'grey'
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.page--
      }
    },
    nextPage() {
      if (this.page < this.totalPages) {
        this.page++
      }
    }
  },
  mounted() {
    this.fetchLogs()
  }
}
</script>

<style scoped>
.whatsapp-logs-container {
  font-family: 'Outfit', 'Inter', sans-serif;
}
.stat-card {
  backdrop-filter: blur(10px);
}
.reply-bubble::after {
  content: '';
  position: absolute;
  top: 12px;
  left: -6px;
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-right: 6px solid rgba(253, 244, 245, 0.7); /* pink-50/50 fallback */
  transform: rotate(0deg);
}
</style>
